import { useCallback } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-hook';

import { env } from '@/env';
import { useAnalytics } from '@/hooks/useAnalytics';
import { RECAPTCHA_VALIDATION_FAILED } from '@/consts/events';

interface UseReCaptchaOptions {
  screen: string;
}

interface UseReCaptchaReturn {
  executeReCaptcha: (action: string) => Promise<string>;
  isReCaptchaEnabled: boolean;
}

/**
 * Hook for Google reCAPTCHA v3 integration.
 * Returns a function to execute reCAPTCHA and get a token.
 *
 * Usage:
 * ```
 * const { executeReCaptcha } = useReCaptcha({ screen: 'signup' });
 * const token = await executeReCaptcha('submit_form');
 * // Send token to backend for validation
 * ```
 */
export const useReCaptcha = ({ screen }: UseReCaptchaOptions): UseReCaptchaReturn => {
  const { executeGoogleReCaptcha } = useGoogleReCaptcha(env.RECAPTCHA_SITE_KEY, {
    recaptchaNet: true,
  });

  const { trackError } = useAnalytics();

  const executeReCaptcha = useCallback(
    async (action: string): Promise<string> => {
      try {
        const token = await executeGoogleReCaptcha(action);
        return token;
      } catch (error) {
        trackError({
          event: RECAPTCHA_VALIDATION_FAILED,
          properties: {
            action,
            screen,
            error: error instanceof Error ? error.message : 'Unknown error',
          },
        });
        throw error;
      }
    },
    [executeGoogleReCaptcha, screen, trackError]
  );

  return {
    executeReCaptcha,
    isReCaptchaEnabled: !!env.RECAPTCHA_SITE_KEY,
  };
};
